import { Link } from "react-router-dom";
import { Zap, TrendingUp, Maximize, Check, ArrowRight, Star } from "lucide-react";

const ComboMachines = () => {
  const features = [
    "Drinks AND snacks in one unit",
    "Up to 500+ total selections",
    "Space-efficient design",
    "Most popular choice",
    "Flexible layout options",
    "Digital payment ready",
  ];

  return (
    <div className="min-h-screen" data-testid="combo-machines-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-navy relative overflow-hidden" data-testid="combo-hero">
        <div className="absolute top-20 right-20 w-10 h-10 bg-gold opacity-60" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                <span className="inline-block bg-gold text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy">
                  COMBO MACHINES
                </span>
                <span className="inline-flex items-center gap-1 bg-coral text-cream font-mono text-sm px-3 py-2 rounded-full border-2 border-cream">
                  <Star className="h-3 w-3 fill-cream" /> MOST POPULAR
                </span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl text-cream mb-4">
                The Best of <span className="text-coral">Both Worlds</span>
              </h1>
              <p className="text-cream/80 text-lg mb-8">
                Why choose between drinks and snacks? Our combo machines deliver
                maximum variety in a single, space-saving unit.
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="combo-cta"
              >
                Get a Combo Machine <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
            <div className="flex justify-center">
              <div className="bg-coral/20 rounded-2xl p-8 border-2 border-cream/20">
                <Zap className="h-32 w-32 text-cream mx-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-32" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-navy mb-4">
              Why Combo Machines Win
            </h2>
            <p className="text-navy/70 max-w-2xl mx-auto">
              Our most requested solution. Here's why property managers love combo
              machines.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Space Efficient */}
            <div className="card-retro text-center" data-testid="benefit-space">
              <div className="bg-coral w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Maximize className="h-8 w-8 text-cream" />
              </div>
              <h3 className="font-heading text-xl text-navy mb-2">Space Efficient</h3>
              <p className="text-navy/70">One machine footprint, double the variety</p>
            </div>

            {/* Higher Satisfaction */}
            <div className="card-retro text-center" data-testid="benefit-satisfaction">
              <div className="bg-coral w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-cream" />
              </div>
              <h3 className="font-heading text-xl text-navy mb-2">Higher Satisfaction</h3>
              <p className="text-navy/70">Users get drinks AND snacks in one stop</p>
            </div>

            {/* Best Value */}
            <div className="card-retro text-center" data-testid="benefit-value">
              <div className="bg-coral w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-cream" />
              </div>
              <h3 className="font-heading text-xl text-navy mb-2">Best Value</h3>
              <p className="text-navy/70">Maximum variety with minimal space</p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-20 md:py-32 bg-gold/20" data-testid="whats-inside-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-cream text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-6">
                WHAT'S INSIDE
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-navy mb-6">
                Everything in One Machine
              </h2>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white rounded-xl border-2 border-navy p-4 text-center shadow-[4px_4px_0px_0px_#1A2A3A]">
                  <div className="font-heading text-3xl text-coral mb-1">250+</div>
                  <p className="text-navy/70 text-sm">Drink Options</p>
                </div>
                <div className="bg-white rounded-xl border-2 border-navy p-4 text-center shadow-[4px_4px_0px_0px_#1A2A3A]">
                  <div className="font-heading text-3xl text-coral mb-1">250+</div>
                  <p className="text-navy/70 text-sm">Snack Options</p>
                </div>
              </div>
              <ul className="space-y-3">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="bg-matcha w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="h-4 w-4 text-cream" />
                    </div>
                    <span className="text-navy">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl border-2 border-navy p-8 shadow-[8px_8px_0px_0px_#E07A5F]">
              <div className="text-center">
                <div className="font-mono text-coral text-sm mb-2">TOTAL CAPACITY</div>
                <div className="font-heading text-6xl text-navy mb-4">500+</div>
                <p className="text-navy/70">Combined selections in one unit</p>
                <div className="mt-6 pt-6 border-t-2 border-navy/10">
                  <p className="text-sm text-navy/60">
                    Perfect for offices, apartments, and commercial spaces
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-coral" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-cream mb-4">
            The Smart Choice for Your Space
          </h2>
          <p className="text-cream/90 mb-8 max-w-xl mx-auto">
            Get the most popular vending solution at zero cost.
          </p>
          <Link
            to="/contact"
            className="btn-secondary inline-block"
            data-testid="cta-button"
          >
            Request Your Combo Machine
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ComboMachines;
